/**
 * HTTP interceptor for capturing outbound HTTP calls during message handling.
 *
 * Uses Node.js diagnostics_channel to subscribe to undici request events,
 * capturing ALL outbound HTTP from the process (including LLM SDKs, Playwright
 * navigations, and any library that uses undici under the hood).
 *
 * Also monkey-patches globalThis.fetch as a fallback for non-undici HTTP.
 *
 * Uses AsyncLocalStorage to associate intercepted calls with the active
 * message's traceId/parentSpanId.
 *
 * Built-in skip patterns prevent capturing internal infrastructure requests
 * (localhost, AgentVault API, Clerk auth).
 */
import { AsyncLocalStorage } from "node:async_hooks";
import diagnosticsChannel from "node:diagnostics_channel";
import {
  detectLlmEndpoint,
  parseLlmResponse,
  parseLlmSseStream,
  type LlmProvider,
  type ParsedLlmResponse,
} from "./llm-response-parser.js";
import { calculateCost } from "./llm-cost.js";

// ── Public types ──────────────────────────────────────────────────────

export interface HttpCallReport {
  method: string;
  url: string;
  statusCode: number;
  latencyMs: number;
  traceId?: string;
  parentSpanId?: string;
}

export interface LlmCallReport {
  provider: LlmProvider;
  model: string;
  tokensInput: number;
  tokensOutput: number;
  costUsd?: number;
  latencyMs: number;
  url: string;
  traceId?: string;
  parentSpanId?: string;
}

export interface TraceContext {
  traceId: string;
  parentSpanId: string;
}

export interface FetchInterceptorOptions {
  onHttpCall: (report: HttpCallReport) => void;
  /**
   * Optional callback fired when a known LLM API endpoint is intercepted and
   * its response body parses cleanly. Body parsing happens in-process inside
   * the customer's harness; only the extracted metadata (model, tokens, cost)
   * is passed to this callback. Prompt and completion content never leave
   * the customer's process.
   */
  onLlmCall?: (report: LlmCallReport) => void;
  skipPatterns?: RegExp[];
}

// ── Internal state ────────────────────────────────────────────────────

const traceStore = new AsyncLocalStorage<TraceContext>();

const DEFAULT_SKIP_PATTERNS: RegExp[] = [
  /^https?:\/\/localhost(:\d+)?/,
  /^https?:\/\/127\.0\.0\.1(:\d+)?/,
  /\.agentvault\.chat/,
  /\.agentvault\.dev/,
  /\.clerk\./,
];

let installed = false;
let originalFetch: typeof globalThis.fetch | undefined;

// Track in-flight undici requests: request → { url, method, startTime }
const inflightRequests = new WeakMap<
  object,
  { url: string; method: string; startTime: number }
>();

// Diagnostics channel subscriptions
let unsubCreate: (() => void) | undefined;
let unsubHeaders: (() => void) | undefined;
let unsubError: (() => void) | undefined;

// ── Helpers ───────────────────────────────────────────────────────────

function shouldSkip(url: string, extraPatterns: RegExp[]): boolean {
  const allPatterns = [...DEFAULT_SKIP_PATTERNS, ...extraPatterns];
  return allPatterns.some((p) => p.test(url));
}

function extractUrl(origin: string, path: string): string {
  // undici gives us origin (e.g., "https://api.openai.com") and path (e.g., "/v1/chat/completions")
  return `${origin}${path}`;
}

/**
 * Read a cloned LLM API response, extract usage metadata, calculate cost,
 * and dispatch onLlmCall. All errors swallowed — telemetry is best-effort.
 *
 * Branches on Content-Type because modern LLM SDKs default to streaming
 * (Anthropic SDK, OpenAI SDK, OpenRouter via OpenAI SDK). Streaming
 * responses are SSE (`text/event-stream`) where usage metadata appears in
 * a terminal event, not a parseable JSON document.
 */
async function parseAndReportLlm(
  responseClone: Response,
  provider: LlmProvider,
  url: string,
  latencyMs: number,
  ctx: TraceContext | undefined,
  onLlmCall: (r: LlmCallReport) => void,
): Promise<void> {
  try {
    const contentType = responseClone.headers.get("content-type") ?? "";
    let parsed: ParsedLlmResponse | null;

    if (contentType.includes("text/event-stream")) {
      const text = await responseClone.text();
      parsed = parseLlmSseStream(provider, text);
    } else {
      const body = await responseClone.json();
      parsed = parseLlmResponse(provider, body);
    }

    if (!parsed) return;
    const costUsd = calculateCost(parsed.model, parsed.tokensInput, parsed.tokensOutput, provider);
    onLlmCall({
      provider,
      model: parsed.model,
      tokensInput: parsed.tokensInput,
      tokensOutput: parsed.tokensOutput,
      costUsd,
      latencyMs,
      url,
      traceId: ctx?.traceId,
      parentSpanId: ctx?.parentSpanId,
    });
  } catch { /* best-effort: malformed body, network error on body read, etc. */ }
}

// ── Public API ────────────────────────────────────────────────────────

/**
 * Install HTTP interceptor using both undici diagnostics channels and
 * globalThis.fetch monkey-patching. Idempotent.
 */
export function installFetchInterceptor(opts: FetchInterceptorOptions): void {
  if (installed) return;
  installed = true;

  const skipPatterns = [
    ...DEFAULT_SKIP_PATTERNS,
    ...(opts.skipPatterns ?? []),
  ];

  // ── 1. Undici diagnostics channel ──────────────────────────────────
  // undici publishes events for every HTTP request it makes.
  // This captures LLM SDK calls, playwright navigations, etc.

  try {
    const createChannel = diagnosticsChannel.channel("undici:request:create");
    const headersChannel = diagnosticsChannel.channel("undici:request:headers");
    const errorChannel = diagnosticsChannel.channel("undici:request:error");

    const onRequestCreate = (message: unknown) => {
      try {
        const msg = message as {
          request: {
            method?: string;
            origin?: string;
            path?: string;
          };
        };
        const req = msg.request;
        if (!req) return;

        const origin = String(req.origin ?? "");
        const path = String(req.path ?? "/");
        const url = extractUrl(origin, path);

        if (shouldSkip(url, skipPatterns)) return;

        inflightRequests.set(req, {
          url,
          method: String(req.method ?? "GET").toUpperCase(),
          startTime: Date.now(),
        });
      } catch { /* best-effort */ }
    };

    const onRequestHeaders = (message: unknown) => {
      try {
        const msg = message as {
          request: object;
          response: { statusCode?: number };
        };
        const tracked = inflightRequests.get(msg.request);
        if (!tracked) return;
        inflightRequests.delete(msg.request);

        const latencyMs = Date.now() - tracked.startTime;
        const ctx = traceStore.getStore();

        opts.onHttpCall({
          method: tracked.method,
          url: tracked.url,
          statusCode: msg.response?.statusCode ?? 0,
          latencyMs,
          traceId: ctx?.traceId,
          parentSpanId: ctx?.parentSpanId,
        });
      } catch { /* best-effort */ }
    };

    const onRequestError = (message: unknown) => {
      try {
        const msg = message as { request: object; error?: Error };
        const tracked = inflightRequests.get(msg.request);
        if (!tracked) return;
        inflightRequests.delete(msg.request);

        const latencyMs = Date.now() - tracked.startTime;
        const ctx = traceStore.getStore();

        opts.onHttpCall({
          method: tracked.method,
          url: tracked.url,
          statusCode: 0,
          latencyMs,
          traceId: ctx?.traceId,
          parentSpanId: ctx?.parentSpanId,
        });
      } catch { /* best-effort */ }
    };

    createChannel.subscribe(onRequestCreate);
    headersChannel.subscribe(onRequestHeaders);
    errorChannel.subscribe(onRequestError);

    unsubCreate = () => createChannel.unsubscribe(onRequestCreate);
    unsubHeaders = () => headersChannel.unsubscribe(onRequestHeaders);
    unsubError = () => errorChannel.unsubscribe(onRequestError);
  } catch {
    // diagnostics_channel not available or undici not loaded — fall through to fetch-only
  }

  // ── 2. globalThis.fetch monkey-patch (fallback) ────────────────────
  // Catches any HTTP that doesn't go through undici (e.g., native fetch
  // in newer Node.js, or custom HTTP implementations).

  originalFetch = globalThis.fetch;
  const savedOriginal = originalFetch;

  globalThis.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const url =
      input instanceof Request
        ? input.url
        : input instanceof URL
          ? input.href
          : String(input);

    if (shouldSkip(url, skipPatterns)) {
      return savedOriginal(input, init);
    }

    const method =
      input instanceof Request
        ? input.method
        : init?.method ?? "GET";

    const ctx = traceStore.getStore();
    const start = Date.now();

    try {
      const response = await savedOriginal(input, init);
      const latencyMs = Date.now() - start;

      try {
        opts.onHttpCall({
          method,
          url,
          statusCode: response.status,
          latencyMs,
          traceId: ctx?.traceId,
          parentSpanId: ctx?.parentSpanId,
        });
      } catch { /* best-effort */ }

      // LLM endpoint? Clone the response and async-parse usage metadata.
      // Plaintext (prompts, completions) is read in-process and never
      // transmitted — only the extracted token/model/cost metadata reaches
      // the onLlmCall callback.
      const llmProvider = detectLlmEndpoint(url);
      if (llmProvider && response.ok && opts.onLlmCall) {
        const clone = response.clone();
        void parseAndReportLlm(clone, llmProvider, url, latencyMs, ctx, opts.onLlmCall);
      }

      return response;
    } catch (err) {
      const latencyMs = Date.now() - start;

      try {
        opts.onHttpCall({
          method,
          url,
          statusCode: 0,
          latencyMs,
          traceId: ctx?.traceId,
          parentSpanId: ctx?.parentSpanId,
        });
      } catch { /* best-effort */ }

      throw err;
    }
  };
}

/**
 * Uninstall all interceptors. Safe to call even if not installed.
 */
export function uninstallFetchInterceptor(): void {
  if (!installed) return;

  // Restore fetch
  if (originalFetch) {
    globalThis.fetch = originalFetch;
    originalFetch = undefined;
  }

  // Unsubscribe from diagnostics channels
  unsubCreate?.();
  unsubHeaders?.();
  unsubError?.();
  unsubCreate = undefined;
  unsubHeaders = undefined;
  unsubError = undefined;

  installed = false;
}

/**
 * Run an async function with trace context attached via AsyncLocalStorage.
 * Any HTTP calls made within `fn` (via undici or fetch) will include
 * traceId/parentSpanId in their HttpCallReport.
 */
export function runWithTraceContext<T>(
  ctx: TraceContext,
  fn: () => T | Promise<T>,
): Promise<T> {
  return traceStore.run(ctx, fn) as Promise<T>;
}
